'use client';

import { createContext, useContext, useEffect, useState, useCallback, useRef, type ReactNode } from 'react';
import { usePostHog } from 'posthog-js/react';
import { getSupabaseBrowserClient, type User, type Session } from '../../lib/supabase';
import { linkDeviceToUser } from '../../lib/auth';
import { captureError } from '../../lib/error-tracking';
import { localAuthEnabled, safeAuthNext } from '@/lib/local-auth';
import type { AccountProfile } from '@/lib/account-profile';
import { SignInModal } from './SignInModal';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  accountProfile: AccountProfile | null;
  updateAccountProfile: (profile: AccountProfile) => void;
  signInWithGoogle: (redirectTo?: string) => Promise<void>;
  signOut: () => Promise<void>;
  openSignIn: (returnTo?: string) => void;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  loading: true,
  accountProfile: null,
  updateAccountProfile: () => {},
  signInWithGoogle: async () => {},
  signOut: async () => {},
  openSignIn: () => {},
});

export function AuthProvider({ children, initialUser }: { children: ReactNode; initialUser?: User | null }) {
  const [user, setUser] = useState<User | null>(initialUser ?? null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(initialUser === undefined);
  const [signInNext, setSignInNext] = useState<string | null>(null);
  const openSignIn = useCallback((returnTo?: string) => {
    const current = window.location.pathname + window.location.search + window.location.hash;
    setSignInNext(safeAuthNext(returnTo || (current === '/' ? '/upload' : current)));
  }, []);
  const closeSignIn = useCallback(() => {
    setSignInNext(null);
    if (/^\/(upload|account|deck)(\/|$)/.test(window.location.pathname)) window.location.assign('/');
  }, []);
  const posthog = usePostHog();
  useEffect(() => {
    if (loading) return;
    const url = new URL(window.location.href);
    if (url.searchParams.get('signin') !== '1') return;
    const next = safeAuthNext(url.searchParams.get('next'));
    url.searchParams.delete('signin');
    url.searchParams.delete('next');
    window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
    if (user && !user.is_anonymous) window.location.replace(next);
    else queueMicrotask(() => openSignIn(next));
  }, [loading, user, openSignIn]);
  const [profileRecord, setProfileRecord] = useState<{ userId: string; profile: AccountProfile | null }>();
  const profileRevision = useRef(0);
  const accountProfile = profileRecord?.userId === user?.id ? profileRecord?.profile ?? null : null;
  const updateAccountProfile = useCallback((profile: AccountProfile) => {
    if (!user) return;
    profileRevision.current += 1;
    setProfileRecord({ userId: user.id, profile });
  }, [user]);
  useEffect(() => {
    const userId = user?.id;
    if (!userId) { setProfileRecord(undefined); return; }
    const controller = new AbortController();
    const revision = ++profileRevision.current;
    fetch('/api/account/profile', { signal: controller.signal, cache: 'no-store' }).then(async response => {
      if (!response.ok) return;
      const data = await response.json() as { profile: AccountProfile | null };
      if (!controller.signal.aborted && revision === profileRevision.current) setProfileRecord({ userId, profile: data.profile });
    }).catch(() => { /* The private account menu can still fall back to initials. */ });
    return () => controller.abort();
  }, [user?.id]);

  useEffect(() => {
    const supabase = getSupabaseBrowserClient();
    if (!supabase) {
      setLoading(false);
      return;
    }

    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user && !session.user.is_anonymous) setSignInNext(null);
      setLoading(false);

      // Link device_id to user if they just signed in
      if (session?.user) {
        const deviceId = localStorage.getItem('slidetoanki_device_id');
        if (deviceId) {
          linkDeviceToUser(deviceId, session.user.id);
        }
      }
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user && !session.user.is_anonymous) setSignInNext(null);
      setLoading(false);

      // Link device on sign in
      if (session?.user) {
        const deviceId = localStorage.getItem('slidetoanki_device_id');
        if (deviceId) {
          linkDeviceToUser(deviceId, session.user.id);
        }

        // Track sign-in completion
        if (event === 'SIGNED_IN') {
          posthog?.capture('auth_sign_in_completed', {
            provider: 'google',
            user_id: session.user.id,
            $set: { is_authenticated: true },
          });
        }
      }

      // Track sign-out
      if (event === 'SIGNED_OUT') {
        posthog?.capture('auth_sign_out', {
          $set: { is_authenticated: false },
        });
        posthog?.reset();
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const signInWithGoogle = useCallback(async (redirectTo?: string) => {
    const supabase = getSupabaseBrowserClient();
    if (!supabase) {
      throw new Error('Sign-in is currently unavailable.');
    }

    // The SDK constructs an OAuth URL even when a local provider is disabled.
    // Check first so the sign-in page can explain the missing configuration.
    if (localAuthEnabled()) {
      const settings = await fetch(`${process.env.NEXT_PUBLIC_SUPABASE_URL}/auth/v1/settings`, {
        headers: { apikey: process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY || '' },
        cache: 'no-store',
      });
      if (!settings.ok) throw new Error('Could not connect to the local sign-in service.');
      const config = await settings.json() as { external?: { google?: boolean } };
      if (!config.external?.google) throw new Error('Google sign-in needs to be configured for this local environment.');
    }

    const redirectPath = safeAuthNext(redirectTo || window.location.pathname + window.location.search + window.location.hash);
    // Invite fragments and return-query parameters can contain bearer tokens.
    // Preserve them for OAuth navigation, never for analytics or error reports.
    const analyticsRedirectPath = redirectPath.split(/[?#]/)[0];
    posthog?.capture('auth_sign_in_started', {
      provider: 'google',
      redirect_path: analyticsRedirectPath,
    });

    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/auth/callback?next=${encodeURIComponent(redirectPath)}`,
      },
    });

    if (error) {
      console.error('[Auth] Sign in error:', error.message);
      captureError('sign_in_error', error, { provider: 'google', redirect_path: analyticsRedirectPath });
      throw error;
    }
  }, [posthog]);

  const signOut = useCallback(async () => {
    const supabase = getSupabaseBrowserClient();
    if (!supabase) return;

    const { error } = await supabase.auth.signOut();
    if (error) {
      console.error('[Auth] Sign out error:', error.message);
    }
  }, []);

  return (
    <AuthContext.Provider value={{ user, session, loading, accountProfile, updateAccountProfile, signInWithGoogle, signOut, openSignIn }}>
      {children}
      {signInNext !== null && (!user || user.is_anonymous) && <SignInModal returnTo={signInNext} onClose={closeSignIn} onGoogle={signInWithGoogle} />}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
